import { ROTULO_FASES, type Fase, type Perfil, type Permissao } from '@metaepi/shared';

/**
 * Itens do menu. Cada item diz QUEM o vê (perfis) ou qual PERMISSÃO exige.
 * Lembrete: esconder no menu é só conforto; quem bloqueia de verdade é a API.
 */
export interface ItemNavegacao {
  rotulo: string;
  caminho: string;
  modulo: number;
  descricao: string;
  perfis?: readonly Perfil[];
  permissao?: Permissao;
  /** Já funciona (senão a rota mostra "Em construção" e a página inicial, "O que vem a seguir"). */
  pronto?: boolean;
}

const CLINICOS: readonly Perfil[] = ['medico', 'enfermeiro', 'gestor'];

const FASES_MENU: { fase: Fase; modulo: number; descricao: string }[] = [
  {
    fase: 'pre_natal',
    modulo: 4,
    descricao:
      'Painel da unidade: testes, tratamento da gestante e do parceiro com doses agendadas, pendências e controle de cura.',
  },
  {
    fase: 'maternidade',
    modulo: 6,
    descricao:
      'Gestantes internadas, testes de sífilis e HIV da admissão, parto, recém-nascidos e alta.',
  },
  {
    fase: 'neonatologia',
    modulo: 6,
    descricao:
      'Recém-nascidos de mães com sífilis: fluxograma de conduta guiado (D-002), confirmado pelo médico.',
  },
  {
    fase: 'puericultura',
    modulo: 7,
    descricao:
      'Seguimento da criança exposta: VDRL com 1, 3, 6, 12 e 18 meses e encaminhamentos (D-003).',
  },
];

/** Fases que já têm painel (Módulo 4: pré-natal; Módulo 6: maternidade e neonatologia; Módulo 7: puericultura). */
const PERMISSAO_FASE: Partial<Record<Fase, Permissao>> = {
  pre_natal: 'prenatal:ver',
  maternidade: 'maternidade:ver',
  neonatologia: 'maternidade:ver',
  puericultura: 'puericultura:ver',
};

export const ITENS_FASES: ItemNavegacao[] = FASES_MENU.map(({ fase, modulo, descricao }) =>
  PERMISSAO_FASE[fase]
    ? {
        rotulo: ROTULO_FASES[fase],
        caminho: `/linha-cuidado/${fase}`,
        modulo,
        descricao,
        permissao: PERMISSAO_FASE[fase],
        pronto: true,
      }
    : {
        rotulo: ROTULO_FASES[fase],
        caminho: `/linha-cuidado/${fase}`,
        modulo,
        descricao,
        perfis: CLINICOS,
      },
);

export const ITENS_PRINCIPAIS: ItemNavegacao[] = [
  {
    rotulo: 'Gestantes e parceiros',
    caminho: '/pacientes',
    modulo: 2,
    descricao: 'Cadastro e busca por CPF, CNS, parte do nome ou data de nascimento.',
    permissao: 'pacientes:ver',
    pronto: true,
  },
  {
    rotulo: 'Notificações SINAN',
    caminho: '/notificacoes',
    modulo: 5,
    descricao:
      'Fichas SINAN preenchidas automaticamente a partir do prontuário, com download do PDF oficial (D-005).',
    permissao: 'notificacoes:ver',
    pronto: true,
  },
  {
    rotulo: 'Agenda e pendências',
    caminho: '/agenda',
    modulo: 8,
    descricao:
      'Doses, coletas e consultas por período, com os atrasados, e as pendências de todas as fases.',
    permissao: 'agenda:ver',
    pronto: true,
  },
  {
    rotulo: 'Indicadores',
    caminho: '/indicadores',
    modulo: 8,
    descricao: 'Indicadores oficiais de eliminação da transmissão vertical, com metas e selos.',
    permissao: 'indicadores:ver',
    pronto: true,
  },
];

export const ITENS_ADMINISTRACAO: ItemNavegacao[] = [
  {
    rotulo: 'Pedidos do titular (LGPD)',
    caminho: '/titular',
    modulo: 9,
    descricao: 'Acesso, cópia e relatório de quem acessou os dados, com prazo de 15 dias.',
    permissao: 'titular:atender',
    pronto: true,
  },
  {
    rotulo: 'Usuários',
    caminho: '/administracao/usuarios',
    modulo: 1,
    descricao: 'Profissionais, perfis e links de definição de senha.',
    permissao: 'usuarios:gerenciar',
  },
  {
    rotulo: 'Unidades de saúde',
    caminho: '/administracao/unidades',
    modulo: 1,
    descricao: 'UBS, maternidades, hospitais e Casa da Gestante.',
    permissao: 'unidades:gerenciar',
  },
  {
    rotulo: 'Municípios',
    caminho: '/administracao/municipios',
    modulo: 1,
    descricao: 'Municípios participantes e data de adesão (D-010).',
    permissao: 'municipios:gerenciar',
  },
  {
    rotulo: 'Pedidos de ajuda',
    caminho: '/administracao/pedidos',
    modulo: 1,
    descricao: 'Profissionais que não lembram o e-mail de acesso.',
    permissao: 'solicitacoes:atender',
  },
  {
    rotulo: 'Auditoria',
    caminho: '/administracao/auditoria',
    modulo: 1,
    descricao: 'Quem alterou o quê, e quando.',
    permissao: 'auditoria:ver',
  },
  {
    rotulo: 'Registro de acessos',
    caminho: '/administracao/acessos',
    modulo: 1,
    descricao: 'Logins, falhas e bloqueios com IP e localização aproximada (D-008).',
    permissao: 'acessos:ver',
  },
  {
    rotulo: 'Sessões ativas',
    caminho: '/administracao/sessoes',
    modulo: 1,
    descricao: 'Quem está conectado agora; encerramento remoto.',
    permissao: 'sessoes:gerenciar',
  },
  {
    rotulo: 'Módulos disponíveis',
    caminho: '/administracao/modulos',
    modulo: 1,
    descricao: 'Liberar hepatites e Chagas quando estiverem prontos (D-014).',
    permissao: 'parametros:gerenciar',
  },
];

/** Agravos aparecem para os perfis clínicos. */
export const PERFIS_AGRAVOS = CLINICOS;

export function itemVisivel(
  item: ItemNavegacao,
  perfil: Perfil,
  pode: (p: Permissao) => boolean,
): boolean {
  if (item.permissao) return pode(item.permissao);
  return item.perfis ? item.perfis.includes(perfil) : true;
}
